import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import {
  ApplePay,
  UIElement,
  SubmitData,
  AddressData,
  SubmitActions,
  PaymentAction,
  AdditionalDetailsData,
  AdyenCheckoutError,
} from '@adyen/adyen-web';

import {
  APPLE_PAY_CONFIG,
  AdyenCheckoutErrorTypes,
  ApplePayPaymentDetailsRef,
  createAdyenApplePayFinalizeRequestBody,
  mapAdyenSdkErrorToAppchargeError,
} from '../utils';
import {
  classifyApplePayCancel,
  installApplePayMerchantValidationProbe,
  resetApplePayCancelDiagnostics,
} from '../apple-pay-cancel-diagnostics';
import {
  ESdkEvents,
  MixpanelEvents,
  EPaymentProvider,
  EBEPaymentMethodsName,
  AdyenFinalizePaymentResponse,
  AppchargeErrorType,
  GetAdyenPaymentStatusResponse,
} from '../../../../../../../contracts';
import { sendFEEvent } from '../../../../../../../eventUtil';
import { useAPI } from '../../../../../../../services/api.service';
import { useCheckout } from '../../../../../../../hooks/use-checkout';
import { usePaymentState } from '../../../../../hooks/use-payment-state';
import { useOrderPolling } from '../../../../../hooks/use-order-polling';
import { useAdyenCheckout } from '../../../../AdyenPaymentElement/hooks/use-adyen-checkout';
import { ADYEN_CARD_CONFIGURATION } from '../../../../AdyenPaymentElement/utils';
import { generateAppchargeErrorMessage } from '../../../../../../../utils/generate-appcharge-error-message';
import { apmLatencyTracker } from '../../../../../../../utils/apm-latency-tracker';
import { sendMixpanelEvent } from '../../../../../../../utils/mixpanel-utils';
import { logError, logInfo } from '../../../../../../../utils/coralogix-rum';
import { normalizeToAdyenAmount } from '../../../../../../../utils/normalize-to-adyen-amount';

export function UseAdyenApplePay(onReady?: (isAvailable: boolean) => void) {
  const isAdyenPaymentInProgress = useRef(false);
  const isPaySessionStartingRef = useRef(false);
  const pendingErrorTypeRef = useRef<AppchargeErrorType | undefined>(undefined);
  const pendingActionRef = useRef<PaymentAction | null>(null);
  const { startOrderPolling } = useOrderPolling();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });

  const { checkoutData, getEventParams, getMixpanelEventParams } = useCheckout();
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const priceBreakdown = usePaymentState((state) => state.priceBreakdown);
  const applePaymentDetailsRef = useRef<ApplePayPaymentDetailsRef>();
  const [applePay, setApplePay] = useState<UIElement | null>(null);
  const [threeDSComponent, setThreeDSComponent] = useState<UIElement | null>(null);
  const { API } = useAPI();
  const applePayPM = useMemo(
    () =>
      checkoutData?.paymentMethods.find(
        (pm) =>
          pm.provider === EPaymentProvider.adyen &&
          pm.paymentMethodName === EBEPaymentMethodsName.apple,
      ),
    [checkoutData?.paymentMethods],
  );
  const { checkoutInstance } = useAdyenCheckout(applePayPM);

  const currencyCode = checkoutData?.pricing?.currencyCode || '';

  const onAuthorized = (
    data: {
      authorizedEvent: ApplePayJS.ApplePayPaymentAuthorizedEvent;
      billingAddress?: Partial<AddressData>;
      deliveryAddress?: Partial<AddressData>;
    },
    actions: {
      resolve: () => void;
      reject: (error?: ApplePayJS.ApplePayError) => void;
    },
  ) => {
    const applePayPayment = data.authorizedEvent.payment;
    applePaymentDetailsRef.current = {
      email: applePayPayment.shippingContact?.emailAddress || '',
      paymentMethodDetails: applePayPayment.token.paymentMethod,
    };
    // Payment is authorized; a later sheet-close CANCEL must not reuse the
    // merchant-validation timing snapshot (ACDEV-25945 false positives).
    resetApplePayCancelDiagnostics();
    actions.resolve();
  };

  const onSubmit = async (state: SubmitData, _: UIElement, actions: SubmitActions) => {
    if (isAdyenPaymentInProgress.current) {
      return;
    }
    isAdyenPaymentInProgress.current = true;
    setIsPaymentLoading(true);
    try {
      const finalizePaymentResponse = await API.finalizePayment<AdyenFinalizePaymentResponse>(
        createAdyenApplePayFinalizeRequestBody(
          state.data,
          applePaymentDetailsRef.current!,
          applePayPM!,
        ),
      );
      handleServerResponse(finalizePaymentResponse, actions);
    } catch {
      pendingErrorTypeRef.current = undefined;
      actions.reject();
    }
  };

  const onPaymentFailed = () => {
    handleError();
  };

  const sendPayButtonClickedMixpanelEvent = () => {
    sendMixpanelEvent(MixpanelEvents.ON_PAY_BUTTON_CLICKED, {
      ...getMixpanelEventParams(),
      paymentMethodName: applePayPM?.paymentMethodDisplayName,
    });
  };

  const onPaymentCompleted = () => {
    // When the BE responded with status: 'redirect' + action, we resolved the
    // Apple Pay sheet WITHOUT the action so it would close. The SDK then
    // fires onPaymentCompleted prematurely; mount our 3DS modal here
    // instead of treating this as a real success.
    if (pendingActionRef.current) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      handle3DSFlow(action);
      return;
    }
    handleSuccess();
  };

  const handleSuccess = () => {
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
      ...getEventParams(),
      paymentMethodName: applePayPM?.paymentMethodDisplayName,
    });
    isPaySessionStartingRef.current = false;
    startOrderPolling(checkoutData?.orderId!, {
      paymentMethodName: applePayPM?.paymentMethodDisplayName,
    });
  };

  const handleError = (appChargeErrorType?: AppchargeErrorType, sdkError?: AdyenCheckoutError) => {
    const errorMessage = generateAppchargeErrorMessage(t, appChargeErrorType);
    if (sdkError) {
      logError('Adyen Apple Pay error', sdkError, {
        paymentMethodId: applePayPM?.id,
        paymentMethodName: applePayPM?.paymentMethodName,
        paymentMethodDisplayName: applePayPM?.paymentMethodDisplayName,
        sdkErrorName: sdkError.name,
        appChargeErrorType,
      });
    }
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
      ...getEventParams(),
      paymentMethodName: applePayPM?.paymentMethodDisplayName,
    });
    setErrorMessage(errorMessage);
    setIsPaymentLoading(false);
    setThreeDSComponent(null);
    pendingActionRef.current = null;
    isAdyenPaymentInProgress.current = false;
    isPaySessionStartingRef.current = false;
  };

  const handleCancel = () => {
    setIsPaymentLoading(false);
    setThreeDSComponent(null);
    pendingActionRef.current = null;
    isAdyenPaymentInProgress.current = false;
    isPaySessionStartingRef.current = false;
  };

  // ACDEV-25945: An Apple Pay merchant-validation / domain failure (e.g. Safari
  // Private/Incognito Browsing inside a cross-origin iframe) surfaces as the
  // SAME Adyen CANCEL as a genuine user dismiss. Classify it from the
  // merchant-validation snapshot so RUM tells "Apple Pay not valid" apart from
  // a real user cancel.
  const logApplePayCancel = (sdkError: AdyenCheckoutError) => {
    const diagnostics = classifyApplePayCancel();
    const context = {
      paymentMethodId: applePayPM?.id,
      paymentMethodName: applePayPM?.paymentMethodName,
      paymentMethodDisplayName: applePayPM?.paymentMethodDisplayName,
      sdkErrorName: sdkError.name,
      ...diagnostics,
    };

    const isApplePayNotValid =
      diagnostics.classification === 'domain_mismatch' ||
      diagnostics.classification === 'merchant_validation_rejected';

    if (isApplePayNotValid) {
      logError(
        'Apple Pay not valid: Adyen merchant validation failed and surfaced as a CANCEL ' +
          '(likely Safari Private/Incognito Browsing in a cross-origin iframe - the merchant ' +
          'domain could not be validated)',
        sdkError,
        context,
      );
    } else {
      logInfo('Apple Pay cancelled by user', context);
    }
  };

  useEffect(() => {
    try {
      apmLatencyTracker.startTracking('Apple Pay', applePayPM?.id);
    } catch (error) {
      console.error('Failed to start APM latency tracking:', error);
    }

    // ACDEV-25945: wrap completeMerchantValidation so a later CANCEL can be
    // classified (domain failure vs PassKit rejection vs real user cancel).
    installApplePayMerchantValidationProbe();

    let applePay: ApplePay;
    if (checkoutInstance) {
      applePay = new ApplePay(checkoutInstance, {
        ...APPLE_PAY_CONFIG,
        amount: {
          value: normalizeToAdyenAmount(priceBreakdown?.priceTotal || 0, currencyCode),
          currency: currencyCode,
        },
        configuration: applePayPM?.paymentMethodConfig?.configuration,
        onClick: (resolve) => {
          if (isPaySessionStartingRef.current) {
            resolve();
            return;
          }
          isPaySessionStartingRef.current = true;
          // Fresh session: drop any snapshot from a previous sheet so a later
          // CANCEL isn't misclassified against stale merchant-validation data.
          resetApplePayCancelDiagnostics();
          sendFEEvent(ESdkEvents.ON_PAYMENT_INTENT, {
            ...getEventParams(),
            paymentMethodName: applePayPM?.paymentMethodDisplayName,
          });
          sendPayButtonClickedMixpanelEvent();
          resolve();
        },
        onAuthorized,
        onSubmit,
        onPaymentFailed,
        onPaymentCompleted,
        onError: (error) => {
          console.log('onError Apple Pay', { error });
          if (error.name === AdyenCheckoutErrorTypes.CANCEL) {
            logApplePayCancel(error);
            handleCancel();
            return;
          }
          // Prefer the BE-reported error type if one was stashed by handleServerResponse;
          // otherwise fall back to mapping the Adyen SDK error name (ACDEV-24170).
          const errorType =
            pendingErrorTypeRef.current ?? mapAdyenSdkErrorToAppchargeError(error.name);
          pendingErrorTypeRef.current = undefined;
          handleError(errorType, error);
        },
      });

      applePay
        ?.isAvailable()
        .then(() => onReady?.(true))
        .catch(() => {
          setApplePay(null);
          onReady?.(false);
        })
        .finally(() => {
          try {
            apmLatencyTracker.endTracking('Apple Pay', applePayPM?.id, {
              paymentMethodName: applePayPM?.paymentMethodName,
              paymentMethodDisplayName: applePayPM?.paymentMethodDisplayName,
              provider: 'Adyen',
              type: 'Express',
            });
          } catch (error) {
            console.error('Failed to end APM latency tracking:', error);
          }
        });

      setApplePay(applePay);
    }
  }, [checkoutInstance, priceBreakdown?.priceTotal, applePayPM?.id]);

  const handleServerResponse = (
    finalizePaymentResponse: AdyenFinalizePaymentResponse,
    actions: SubmitActions,
  ) => {
    const { status, resultCode, action } = finalizePaymentResponse.adyen;
    if (status === 'succeeded') {
      actions.resolve({ resultCode: resultCode, action });
    } else if (status === 'redirect') {
      if (action) {
        // Stash the action and resolve WITHOUT it. The Adyen SDK will close
        // the Apple Pay sheet, then fire onPaymentCompleted where we mount
        // our own 3DS modal (separate instance, same approach as the card flow).
        pendingActionRef.current = action;
      }
      actions.resolve({ resultCode });
    } else if (status === 'failed') {
      pendingErrorTypeRef.current = finalizePaymentResponse.appChargeErrorType;
      actions.reject();
    }
  };

  const handle3DSFlow = (action: PaymentAction) => {
    const actionComponent = checkoutInstance!.createFromAction(action, {
      ...ADYEN_CARD_CONFIGURATION,
      onAdditionalDetails,
    });
    setThreeDSComponent(actionComponent);
  };

  const onAdditionalDetails = async (state: AdditionalDetailsData) => {
    const threeDSResult = state.data.details.threeDSResult;
    setThreeDSComponent(null);

    if (!threeDSResult) {
      handleError();
      return;
    }

    try {
      const paymentStatusResponse = await API.getPaymentStatus_NEW<GetAdyenPaymentStatusResponse>(
        threeDSResult,
        applePayPM?.id as string,
      );
      if (paymentStatusResponse.resultCode === 'Authorised') {
        handleSuccess();
      } else {
        handleError();
      }
    } catch {
      handleError();
    }
  };

  return {
    applePay,
    threeDSComponent,
  };
}
